import { all, put, takeLatest, call, select } from 'redux-saga/effects'
import { config } from '../config'
import { generateRecaptchaToken } from '../helpers/recaptcha'
import {
  cardinalCruiseUpdate,
  cardinalCruiseSelector,
} from '../redux/slice/cardinalCruiseSlice'
import {
  authoriseError,
  updateAuthoriseResponse,
  updatePaymentPending,
} from '../redux/slice/paymentResponseSlice'
import { cardsSelector } from '../redux/slice/cardsSlice'
import { paymentSelector } from '../redux/slice/paymentSlice'
import { transactionDetailsSelector } from '../redux/slice/transactionDetailsSlice'
import { selectIsPciFormEnabled } from '../redux/selectors/evervault'
import { applyHookBillingAddressOverride } from '../helpers/billingAddress'
import { CARDINAL_STATUSES } from '../constants/cardinal'
import { authorise } from '../api/payments'
import {
  ERROR_RESPONSES,
  SUCCESS_RESPONSES,
} from '../constants/serverResponses'
import store from '../redux/store'
import { ACTIONS } from '../stores/actions'
import { DataDog } from '../datadog'
import { prepareMetadata } from '../hooks/useMetadata'
import { collectDeviceInformation } from '../helpers/deviceInformation'

const PAYMENT_PAY = 'payment/paymentPay'

const preparePaymentData = ({
  cvv,
  saveCard,
  cardEncryptedData,
  notes,
  cardBin,
  cardholderName,
}) => ({
  cvv,
  saveCard,
  cardEncryptedData,
  notes,
  cardBin,
  cardholderName,
})

function* handleAuthorise(data, isPciEnabled, iframeRef) {
  const {
    paymentData,
    cardinalSessionId,
    cardinalJwt,
    selectedCard,
    billingAddress,
    overrideAmount,
    onSuccess,
    onFailure,
    metadata,
    recaptcha,
  } = data
  try {
    if (metadata === 'invalid') {
      DataDog.error(`Wrong format of metadata`)
      console.error(`Wrong format of metadata`)
    } else if (metadata === 'lengthError') {
      console.error('Metadata should be not greater than 100 characters')
    }
    const { hideBillingAddress, is3dsRequired } = yield select(
      transactionDetailsSelector,
    )
    const isMotoTransaction = !is3dsRequired
    const deviceInformation = collectDeviceInformation()
    let responseData
    let responseStatus

    if (isPciEnabled) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'MFEAuthorise',
          data: {
            ...paymentData,
            cardinalSessionId,
            cardinalJWT: cardinalJwt,
            savedCardId: selectedCard?.id,
            billingAddress: hideBillingAddress
              ? { email: billingAddress.email }
              : billingAddress,
            metadata,
            browserScreenWidth: !isMotoTransaction
              ? window.innerWidth
              : undefined,
            browserScreenHeight: !isMotoTransaction
              ? window.innerHeight
              : undefined,
            amount: overrideAmount || undefined,
            paymentUrl: window.location.href,
            device_information: deviceInformation,
          },
        },
        '*',
      )

      const handleAuthoriseResponse = e => {
        const type = e?.data?.type

        if (type !== 'MFEAuthoriseResponse') return

        responseData = e.data.data.responseData
        responseStatus = e.data.data.responseStatus

        if (responseStatus === SUCCESS_RESPONSES.OK) {
          store.dispatch(
            updateAuthoriseResponse({
              ...responseData,
              responseStatus,
            }),
          )
          onSuccess()
        } else {
          const isPaymentTimeout =
            responseStatus === ERROR_RESPONSES.TIMEOUT_ERROR

          store.dispatch(
            authoriseError({
              responseStatus,
              errorCode: responseData?.errorCode,
              errorMessage: responseData?.errorMessage,
              isRetryable: responseData?.isRetryable || isPaymentTimeout,
            }),
          )
          onFailure()
        }

        window.removeEventListener('message', handleAuthoriseResponse)
      }

      yield call(window.addEventListener, 'message', handleAuthoriseResponse)
    } else {
      let recaptchaToken

      if (window.grecaptcha) {
        try {
          recaptchaToken = yield call(
            generateRecaptchaToken,
            config.recaptchaSiteKey,
            recaptcha.action,
          )
        } catch (error) {
          console.error('Failed to get reCAPTCHA token:', error)
        }
      }

      const response = yield call(authorise, {
        ...paymentData,
        cardinalSessionId,
        cardinalJWT: cardinalJwt,
        savedCardId: selectedCard?.id,
        billingAddress: hideBillingAddress
          ? { email: billingAddress.email }
          : billingAddress,
        metadata,
        browserScreenWidth: window.innerWidth,
        browserScreenHeight: window.innerHeight,
        amount: overrideAmount || undefined,
        paymentUrl: window.location.href,
        device_information: deviceInformation,
        recaptchaToken,
      })

      responseData = response.responseData
      responseStatus = response.responseStatus

      if (responseStatus === SUCCESS_RESPONSES.OK) {
        yield put(
          updateAuthoriseResponse({
            ...responseData,
            responseStatus,
          }),
        )
        yield call(onSuccess)
      } else {
        const isPaymentTimeout =
          responseStatus === ERROR_RESPONSES.TIMEOUT_ERROR

        yield put(
          authoriseError({
            responseStatus,
            errorCode: responseData?.errorCode,
            errorMessage: responseData?.errorMessage,
            isRetryable: responseData?.isRetryable || isPaymentTimeout,
          }),
        )
        yield call(onFailure)
      }
    }
  } catch (e) {
    console.error(e)
    yield put(ACTIONS.PAYMENT_AUTHORISE_ERROR)
    yield call(onFailure)
  }
}

function* handlePayment({
  payload: { onSuccess, onFailure, iframeRef, overrideValues },
}) {
  const payment = yield select(paymentSelector)
  const {
    billingAddress,
    hookBillingAddressOverride,
    overrideAmount,
    is3dsRequired,
    metadata,
    recaptcha,
  } = yield select(transactionDetailsSelector)
  // Evervault > PCI > default: authorise via the PCI iframe only when the PCI
  // form is the active one (false when Evervault takes priority).
  const isPciEnabled = yield select(selectIsPciFormEnabled)

  const { cardinalSessionId, cardinalJwt } = yield select(
    cardinalCruiseSelector,
  )
  const { selectedCard } = yield select(cardsSelector)

  const cardNumber = selectedCard ? selectedCard.bin : payment.cardNumber
  const paymentData = preparePaymentData(payment)

  // Apply accumulated hook overrides (onInitialised / onMethodSelecting /
  // onMethodSelected) on top of the current billing address so they remain
  // authoritative regardless of the payment entry path, then layer the
  // onAuthorising return value last (override wins).
  let hookOverrideBillingAddress = applyHookBillingAddressOverride(
    billingAddress,
    hookBillingAddressOverride,
  )

  if (overrideValues?.billingAddress) {
    hookOverrideBillingAddress = {
      ...hookOverrideBillingAddress,
      ...overrideValues.billingAddress,
    }
  }

  const data = {
    paymentData,
    cardinalSessionId: cardinalSessionId || undefined,
    cardinalJwt: cardinalJwt || undefined,
    selectedCard,
    billingAddress: hookOverrideBillingAddress,
    overrideAmount,
    onSuccess,
    onFailure,
    metadata: prepareMetadata(metadata),
    recaptcha,
  }

  if (!is3dsRequired) {
    yield call(handleAuthorise, data, isPciEnabled, iframeRef)
    return
  }

  try {
    DataDog.info("Cardinal start 'bin.process'")
    window.Cardinal.trigger('bin.process', cardNumber)
      .then(cardinalResult => {
        if (cardinalResult.Status) {
          DataDog.info(
            "Cardinal 'bin.process' Bin profiling success",
            cardinalResult,
          )
        } else {
          DataDog.info(
            "Cardinal 'bin.process' Bin profiling failed",
            cardinalResult,
          )
        }
      })
      .catch(e =>
        DataDog.warn("Cardinal 'bin.process' exception in Bin profiling", e),
      )

    yield call(handleAuthorise, data, isPciEnabled, iframeRef)
  } catch (e) {
    yield put(updatePaymentPending(true))

    yield put(
      cardinalCruiseUpdate({
        cardinalStatus: CARDINAL_STATUSES.failure,
      }),
    )

    yield call(onFailure)
  }
}

export default function* paymentSaga() {
  yield all([takeLatest(PAYMENT_PAY, handlePayment)])
}
